import React, { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Highlighter, Check, Plus, X } from 'lucide-react';
import { Button } from './button';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from './select';
import { Badge } from './badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './dialog';
import { Input } from './input';
import { Label } from './label';
import { Textarea } from './textarea';
import { useToast } from '@/hooks/use-toast';
import { Separator } from './separator';

interface SelectOption {
  value: string;
  label: string;
  isCustom?: boolean;
}

interface SelectOptionGroup {
  label: string;
  options: SelectOption[];
}

interface AddableSelectProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  options?: (SelectOption | SelectOptionGroup)[];
  className?: string;
  disabled?: boolean;
  isHighlighted?: boolean;
  onHighlightChange?: (highlighted: boolean) => void;
  equipmentType: 'panel' | 'inverter' | 'battery';
  category?: string; // For inverter subcategories
  onOptionAdded?: () => void; // Callback when new option is added
}

export function AddableSelect({
  value,
  onChange,
  placeholder = 'Select an option',
  options = [],
  className,
  disabled = false,
  isHighlighted: propIsHighlighted = false,
  onHighlightChange,
  equipmentType,
  category,
  onOptionAdded,
}: AddableSelectProps) {
  const [localIsHighlighted, setLocalIsHighlighted] = useState(propIsHighlighted);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newOptionValue, setNewOptionValue] = useState('');
  const [newOptionLabel, setNewOptionLabel] = useState('');
  const [newOptionDescription, setNewOptionDescription] = useState('');
  const [valueManuallyEdited, setValueManuallyEdited] = useState(false);
  // Panel dimension states
  const [panelWidth, setPanelWidth] = useState('');
  const [panelLength, setPanelLength] = useState('');
  const [panelDepth, setPanelDepth] = useState('30'); // Default to 30mm
  // Panel electrical specifications
  const [panelVoc, setPanelVoc] = useState('');
  const [panelIsc, setPanelIsc] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const isHighlighted = propIsHighlighted;
  const hasValue = !!value;

  // Reset form state when dialog opens
  useEffect(() => {
    if (isAddDialogOpen) {
      setNewOptionValue('');
      setNewOptionLabel('');
      setNewOptionDescription('');
      setPanelWidth('');
      setPanelLength('');
      setPanelDepth('30');
      setPanelVoc('');
      setPanelIsc('');
      setValueManuallyEdited(false);
    }
  }, [isAddDialogOpen]);

  const handleSelectChange = (newValue: string) => {
    // Check if the special "add new" option was selected
    if (newValue === '__add_new__') {
      setIsAddDialogOpen(true);
      return;
    }
    onChange?.(newValue);
  };

  const handleHighlightToggle = () => {
    if (!hasValue) return;

    const newHighlightState = !isHighlighted;
    setLocalIsHighlighted(newHighlightState);
    onHighlightChange?.(newHighlightState);
  };

  const handleClear = () => {
    onChange?.('');
    if (isHighlighted) {
      setLocalIsHighlighted(false);
      onHighlightChange?.(false);
    }
  };

  const handleAddNewOption = async () => {
    if (!newOptionValue.trim() || !newOptionLabel.trim()) {
      toast({
        title: 'Missing Information',
        description: 'Please provide both a value and label for the new option.',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Build specifications object for panels
      let specifications = null;
      if (equipmentType === 'panel') {
        specifications = {
          width: panelWidth ? parseInt(panelWidth) : null,
          length: panelLength ? parseInt(panelLength) : null,
          depth: panelDepth ? parseInt(panelDepth) : 30,
          unit: 'mm',
          voc: panelVoc ? parseFloat(panelVoc) : null,
          isc: panelIsc ? parseFloat(panelIsc) : null
        };
      }

      const response = await fetch('/api/equipment-options', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          type: equipmentType,
          category: category || null,
          value: newOptionValue.trim(),
          label: newOptionLabel.trim(),
          description: newOptionDescription.trim() || null,
          specifications: specifications,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to add equipment option');
      }

      const result = await response.json();

      toast({
        title: 'Option Added',
        description: `Successfully added "${newOptionLabel}" to ${equipmentType} options.`,
      });

      // Select the newly added option
      onChange?.(newOptionValue);

      // Clear form and close dialog
      setNewOptionValue('');
      setNewOptionLabel('');
      setNewOptionDescription('');
      setPanelWidth('');
      setPanelLength('');
      setPanelDepth('30');
      setPanelVoc('');
      setPanelIsc('');
      setValueManuallyEdited(false);
      setIsAddDialogOpen(false);

      // Trigger refresh of options
      onOptionAdded?.();
    } catch (error) {
      console.error('Error adding equipment option:', error);
      toast({
        title: 'Failed to Add Option',
        description: error instanceof Error ? error.message : 'An error occurred while adding the option.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Check if option is a group
  const isOptionGroup = (option: SelectOption | SelectOptionGroup): option is SelectOptionGroup => {
    return 'options' in option;
  };

  // Helper to generate a suggested value from label
  const generateValue = (label: string) => {
    return label
      .trim()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/\s+/g, ' '); // Normalize spaces
  };

  return (
    <div className="space-y-1.5">
      <div className="flex gap-2">
        <Select
          value={value}
          onValueChange={handleSelectChange}
          disabled={disabled}
        >
          <SelectTrigger
            className={cn(
              "flex-1",
              isHighlighted && "border-yellow-400 bg-yellow-50 dark:bg-yellow-950/20",
              className
            )}
          >
            <div className="flex items-center justify-between w-full gap-2">
              <SelectValue placeholder={placeholder} />
              {isHighlighted && (
                <Badge
                  variant="secondary"
                  className="bg-yellow-200 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-200 text-xs px-1.5 py-0"
                >
                  Highlighted
                </Badge>
              )}
            </div>
          </SelectTrigger>
          <SelectContent>

            {/* Existing options */}
            {options.map((option, index) => {
              if (isOptionGroup(option)) {
                return (
                  <SelectGroup key={index}>
                    <SelectLabel>{option.label}</SelectLabel>
                    {option.options.map((item) => (
                      <SelectItem key={item.value} value={item.value}>
                        {item.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                );
              } else {
                return (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                );
              }
            })}

            {/* Add new option button at the bottom, subtle style */}
            <div className="border-t mt-1">
              <button
                type="button"
                className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                onClick={() => {
                  setIsAddDialogOpen(true);
                }}
              >
                <Plus className="h-3 w-3" />
                <span>Add Custom {equipmentType === 'panel' ? 'Panel' : equipmentType === 'inverter' ? 'Inverter' : 'Battery'}</span>
              </button>
            </div>
          </SelectContent>
        </Select>

        {hasValue && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleClear}
                  disabled={disabled}
                  className="shrink-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top">
                <p className="text-sm">Clear selection</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                size="sm"
                variant={isHighlighted ? "default" : "outline"}
                onClick={handleHighlightToggle}
                disabled={disabled || !hasValue}
                className={cn(
                  "shrink-0",
                  isHighlighted && "bg-yellow-500 hover:bg-yellow-600 dark:bg-yellow-600 dark:hover:bg-yellow-700"
                )}
              >
                <Highlighter className="h-4 w-4" />
                {isHighlighted && <Check className="h-3 w-3 ml-1" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
              {!hasValue ? (
                <p className="text-sm">Select a value first before highlighting</p>
              ) : isHighlighted ? (
                <p className="text-sm">Click to remove highlight from this field</p>
              ) : (
                <p className="text-sm">Click to highlight this field in the PDF report</p>
              )}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {isHighlighted && (
        <p className="text-xs text-yellow-700 dark:text-yellow-400 ml-1">
          This field will be highlighted in the PDF report
        </p>
      )}

      {/* Add New Option Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>
              Add New {equipmentType === 'panel' ? 'Solar Panel' : equipmentType === 'inverter' ? 'Inverter' : 'Battery'}
            </DialogTitle>
            <DialogDescription>
              Create a custom {equipmentType} option that will be available for all future site surveys.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="new-option-label">
                Display Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="new-option-label"
                placeholder={
                  equipmentType === 'panel'
                    ? 'e.g., "SolarTech Pro 550W Mono"'
                    : equipmentType === 'inverter'
                    ? 'e.g., "PowerMax 10kW Hybrid Inverter"'
                    : 'e.g., "EnergyStore 15kWh LiFePO4"'
                }
                value={newOptionLabel}
                onChange={(e) => {
                  setNewOptionLabel(e.target.value);
                  // Auto-generate value as user types, unless they manually edited the value field
                  if (!valueManuallyEdited) {
                    setNewOptionValue(generateValue(e.target.value));
                  }
                }}
                disabled={isSubmitting}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="new-option-value">
                Internal Value <span className="text-red-500">*</span>
              </Label>
              <Input
                id="new-option-value"
                placeholder="Auto-generated from display name"
                value={newOptionValue}
                onChange={(e) => {
                  setNewOptionValue(e.target.value);
                  setValueManuallyEdited(true);
                }}
                disabled={isSubmitting}
              />
              <p className="text-xs text-muted-foreground">
                This value is used internally and must be unique.
              </p>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="new-option-description">
                Description / Specifications (Optional)
              </Label>
              <Textarea
                id="new-option-description"
                placeholder={
                  equipmentType === 'panel'
                    ? 'e.g., "High-efficiency monocrystalline, 22% efficiency, 25-year warranty"'
                    : equipmentType === 'inverter'
                    ? 'e.g., "Hybrid inverter with battery support, 97% efficiency, WiFi monitoring"'
                    : 'e.g., "Lithium iron phosphate, 10,000 cycles, 10-year warranty"'
                }
                value={newOptionDescription}
                onChange={(e) => setNewOptionDescription(e.target.value)}
                disabled={isSubmitting}
                rows={3}
              />
            </div>

            {/* Panel Dimensions Section - Only show for panels */}
            {equipmentType === 'panel' && (
              <>
                <Separator />
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Panel Dimensions (mm)</Label>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="grid gap-1">
                      <Label htmlFor="panel-width" className="text-xs">
                        Width
                      </Label>
                      <Input
                        id="panel-width"
                        type="number"
                        placeholder="e.g., 1134"
                        value={panelWidth}
                        onChange={(e) => setPanelWidth(e.target.value)}
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="grid gap-1">
                      <Label htmlFor="panel-length" className="text-xs">
                        Length
                      </Label>
                      <Input
                        id="panel-length"
                        type="number"
                        placeholder="e.g., 2279"
                        value={panelLength}
                        onChange={(e) => setPanelLength(e.target.value)}
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="grid gap-1">
                      <Label htmlFor="panel-depth" className="text-xs">
                        Depth
                      </Label>
                      <Input
                        id="panel-depth"
                        type="number"
                        placeholder="30"
                        value={panelDepth}
                        onChange={(e) => setPanelDepth(e.target.value)}
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Optional: Enter the physical dimensions of the panel
                  </p>
                </div>
                <Separator className="my-3" />
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Electrical Specifications</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="grid gap-1">
                      <Label htmlFor="panel-voc" className="text-xs">
                        Voc (V)
                      </Label>
                      <Input
                        id="panel-voc"
                        type="number"
                        step="0.01"
                        placeholder="e.g., 41.12"
                        value={panelVoc}
                        onChange={(e) => setPanelVoc(e.target.value)}
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="grid gap-1">
                      <Label htmlFor="panel-isc" className="text-xs">
                        Isc (A)
                      </Label>
                      <Input
                        id="panel-isc"
                        type="number"
                        step="0.01"
                        placeholder="e.g., 14.29"
                        value={panelIsc}
                        onChange={(e) => setPanelIsc(e.target.value)}
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Optional: Open circuit voltage and short circuit current
                  </p>
                </div>
              </>
            )}

            {category && (
              <div className="text-sm text-muted-foreground">
                <span className="font-medium">Category:</span> {category}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsAddDialogOpen(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleAddNewOption}
              disabled={isSubmitting || !newOptionValue.trim() || !newOptionLabel.trim()}
            >
              {isSubmitting ? 'Adding...' : 'Add Option'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}